import React, { useEffect, useMemo, useState } from "react";
import { FaEye } from "react-icons/fa";

import { getUserActivity } from "../../../APIS"; // ✅ NEW API

const isWithinDateRange = (dateStr, startDate, endDate) => {
  if (!startDate && !endDate) return true;
  if (!dateStr) return false;

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return false;

  if (startDate) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    if (date < start) return false;
  }

  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    if (date > end) return false;
  }

  return true;
};

const ActivityTrackerTable = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");
  const token = userDetails?.token;

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getUserActivity(token); // ✅ NEW API CALL

      if (response.success && Array.isArray(response.data)) {
        setData(response.data);
      } else {
        setData([]);
      }
    } catch (err) {
      setError("Failed to fetch Activity Data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openUserDetail = (id) => {
    window.open(`/admin/activity_logs?${id}`, "_blank");
  };

  const filteredData = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    return data.filter((user) => {
      const matchesSearch =
        !searchTerm || user.name?.toLowerCase().includes(searchTerm);

      const matchesDateRange = isWithinDateRange(
        user.lastActivity,
        dateRange.startDate,
        dateRange.endDate,
      );

      return matchesSearch && matchesDateRange;
    });
  }, [data, search, dateRange]);

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearDateRange = () => {
    setDateRange({
      startDate: "",
      endDate: "",
    });
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h4 className="card-title m-0">Activity Tracker</h4>

        <div className="d-flex flex-wrap align-items-end gap-2">
          <input
            type="date"
            className="form-control w-auto"
            value={dateRange.startDate}
            onChange={(e) => handleDateRangeChange("startDate", e.target.value)}
            title="Start date"
          />
          <input
            type="date"
            className="form-control w-auto"
            value={dateRange.endDate}
            min={dateRange.startDate}
            onChange={(e) => handleDateRangeChange("endDate", e.target.value)}
            disabled={!dateRange.startDate}
            title="End date"
          />
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={clearDateRange}
            disabled={!dateRange.startDate && !dateRange.endDate}
          >
            Clear
          </button>
          <input
            type="text"
            className="form-control w-auto"
            placeholder="Search user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card-body">
        {loading && <p className="text-center fw-bold">Loading...</p>}
        {error && <p className="text-danger text-center">{error}</p>}

        <div className="table-responsive">
          <table className="table table-striped align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Assigned</th>
                <th>Completed</th>
                <th>Ongoing</th>
                <th>Pending</th>
                <th>Created</th>
                <th>Last Activity</th>
                <th>Activity Logs</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((u, i) => (
                  <tr key={u._id}>
                    <td>{i + 1}</td>
                    <td>{u.name}</td>
                    <td>{u.assignedTasks}</td>
                    <td className="text-success fw-bold">{u.completedTasks}</td>
                    <td className="text-warning fw-bold">{u.ongoingTasks}</td>
                    <td className="text-danger fw-bold">{u.pendingTasks}</td>
                    <td>{u.createdTasks}</td>
                    <td>
                      {u.lastActivity
                        ? new Date(u.lastActivity).toLocaleString()
                        : "—"}
                    </td>

                    <td>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => openUserDetail(u._id)}
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ActivityTrackerTable;
